import React from 'react'
import { captureException, withScope } from './buffer'

// Render-prop fallback gets the support reference + a reset() to retry.
export type FallbackProps = {
  reference: string
  reset: () => void
}

type Fallback = React.ReactNode | ((props: FallbackProps) => React.ReactNode)

type Props = {
  fallback: Fallback
  children: React.ReactNode
}

type State = { hasError: boolean; reference: string }

// Short, human-friendly reference shown to the user and attached to the
// Sentry event as the `support_reference` tag. We mint it ourselves rather
// than rely on Sentry's eventId: the SDK loads lazily post-paint (see
// ./init + ./buffer), so no real eventId exists synchronously when we catch.
function makeReference(): string {
  const bytes = new Uint8Array(4)
  // crypto is available in every browser we target; fall back just in case.
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

// Tiny React class boundary that reports to the buffer (drained by Sentry
// after init, see ./buffer + ./init). Replaces `<Sentry.ErrorBoundary>` so
// the @sentry/react SDK can stay out of the eager chunk.
export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, reference: '' }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true, reference: makeReference() }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const reference = this.state.reference || makeReference()
    withScope((scope) => {
      scope.setLevel('error')
      scope.setTag('support_reference', reference)
      scope.setContext('error_boundary', {
        reference,
        componentStack: info.componentStack,
      })
      captureException(error)
    })
  }

  reset = (): void => {
    this.setState({ hasError: false, reference: '' })
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      const { fallback } = this.props
      if (typeof fallback === 'function') {
        return fallback({ reference: this.state.reference, reset: this.reset })
      }
      return fallback
    }
    return this.props.children
  }
}
